import type { CSSProperties } from 'react';
import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';

// Note: h1-h6 are built-in MUI Typography variants and don't need interface declarations.
// Only custom variants require module augmentation.
//
// MUI requires 3 interface augmentations for custom variants:
// 1. TypographyVariants - defines variant styles in theme (required props)
// 2. TypographyVariantsOptions - same but optional, used when creating/extending themes
// 3. TypographyPropsVariantOverrides - enables variants on <Typography variant="..."> prop
declare module '@mui/material/styles' {
  interface TypographyVariants {
    // Body variants
    'body/xl': CSSProperties;
    'body/lg': CSSProperties;
    'body/md': CSSProperties;
    'body/sm': CSSProperties;
    // Button variants
    'button/lg': CSSProperties;
    'button/md': CSSProperties;
    'button/sm': CSSProperties;
    // Label variants
    'label/lg': CSSProperties;
    'label/md': CSSProperties;
    'label/sm': CSSProperties;
    // System variant
    system: CSSProperties;
  }

  interface TypographyVariantsOptions {
    // Body variants
    'body/xl'?: CSSProperties;
    'body/lg'?: CSSProperties;
    'body/md'?: CSSProperties;
    'body/sm'?: CSSProperties;
    // Button variants
    'button/lg'?: CSSProperties;
    'button/md'?: CSSProperties;
    'button/sm'?: CSSProperties;
    // Label variants
    'label/lg'?: CSSProperties;
    'label/md'?: CSSProperties;
    'label/sm'?: CSSProperties;
    // System variant
    system?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    // Body variants
    'body/xl': true;
    'body/lg': true;
    'body/md': true;
    'body/sm': true;
    // Button variants
    'button/lg': true;
    'button/md': true;
    'button/sm': true;
    // Label variants
    'label/lg': true;
    'label/md': true;
    'label/sm': true;
    // System variant
    system: true;
  }
}

export const MuiTypographyTheme = (mode: PaletteMode): Components<Theme>['MuiTypography'] => {
  const { tokens } = themePalette(mode);

  return {
    styleOverrides: {
      root: {
        color: tokens.content.text.neutral.base,
        fontSize: typographyTokens.fontSizes.base,
        fontFamily: typographyTokens.fontFamilies.sans,
        fontWeight: typographyTokens.fontWeights.regular,
        letterSpacing: 'normal',
      },
      h1: {
        fontSize: typographyTokens.fontSizes['8xl'],
        lineHeight: typographyTokens.lineHeights['8xl'],
      },
      h2: {
        fontSize: typographyTokens.fontSizes['6xl'],
        lineHeight: typographyTokens.lineHeights['6xl'],
      },
      h3: {
        fontSize: typographyTokens.fontSizes['4xl'],
        lineHeight: typographyTokens.lineHeights['4xl'],
      },
      h4: {
        fontSize: typographyTokens.fontSizes['2xl'],
        lineHeight: typographyTokens.lineHeights['2xl'],
      },
      h5: {
        fontSize: typographyTokens.fontSizes.lg,
        lineHeight: typographyTokens.lineHeights.lg,
      },
      h6: {
        fontSize: typographyTokens.fontSizes.base,
        lineHeight: typographyTokens.lineHeights.base,
      },
    },
    variants: [
      {
        props: { variant: 'body/xl' },
        style: {
          fontSize: typographyTokens.fontSizes.lg,
          lineHeight: typographyTokens.lineHeights.lg,
        },
      },
      {
        props: { variant: 'body/lg' },
        style: {
          fontSize: typographyTokens.fontSizes.base,
          lineHeight: typographyTokens.lineHeights.base,
        },
      },
      {
        props: { variant: 'body/md' },
        style: {
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
        },
      },
      {
        props: { variant: 'body/sm' },
        style: {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xs,
        },
      },
      {
        props: { variant: 'button/lg' },
        style: {
          fontSize: typographyTokens.fontSizes.base,
          lineHeight: typographyTokens.lineHeights.base,
        },
      },
      {
        props: { variant: 'button/md' },
        style: {
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
        },
      },
      {
        props: { variant: 'button/sm' },
        style: {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xs,
        },
      },
      {
        props: { variant: 'label/lg' },
        style: {
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.xs,
        },
      },
      {
        props: { variant: 'label/md' },
        style: {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xxs,
        },
      },
      {
        props: { variant: 'label/sm' },
        style: {
          fontSize: typographyTokens.fontSizes.xxs,
          lineHeight: typographyTokens.lineHeights.xxs,
        },
      },
      {
        props: { variant: 'system' },
        style: {
          fontSize: typographyTokens.fontSizes.xxs,
          lineHeight: typographyTokens.lineHeights.xxs,
        },
      },
    ],
  };
};
